import { __decorate } from "tslib";
import AppSegmentationsQuery from '@/plugins/graphql/queries/v2/app-segmentations-basic.gql';
import AppSegmentationCreate from '@/plugins/graphql/mutations/v2/app-segmentation-create.gql';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import GraphqlErrorMixin from '@/helpers/vue_mixins/GraphqlErrorMixin';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import { mixins } from 'vue-class-component';
import { Component } from 'vue-property-decorator';
import { flattenDeep } from 'lodash';
import { Mutation } from 'vuex-class';
import { SEGMENT } from '@/helpers/constants/Store';
import { successfulCreate } from '@/helpers/mixins/LocalizationHelper';
import { ConsoleLogger } from '@/helpers/entities/classes/logger/ConsoleLogger';
import SegmentationTypeMixin from '@/helpers/vue_mixins/v2/SegmentationTypeMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
let CustomerSegmentationMixin = class CustomerSegmentationMixin extends mixins(AlertMixin, GraphqlErrorMixin, RouterParamsAppMixin, SegmentationTypeMixin, CleverTapEventsMixin) {
    /**
     * Custom Condition Builder
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version 0.1.6
     *
     * @return {void}
     */
    buildCustomConditions(rules) {
        const customRules = {};
        // Filter Custom Conditions by type
        rules.forEach((obj) => {
            const { category } = obj;
            const key = this.isLocation(obj.category) ? category : `${category}Uuids`;
            if (this.isCategory(obj.category)) {
                // Category UUID List
                const categoryUuids = obj.rules.map((rule) => {
                    return rule.uuids;
                });
                if (categoryUuids.length >= 1) {
                    Object.assign(customRules, {
                        categoryUuids: flattenDeep(categoryUuids),
                    });
                }
                /**
                 * Category Product UUID List
                 * Note that category has optional products
                 */
                const productUuids = obj.rules.map((rule) => rule.products);
                const flattenProductUuids = flattenDeep(productUuids);
                if (flattenProductUuids.length >= 1) {
                    Object.assign(customRules, { productUuids: flattenProductUuids });
                }
            }
            else if (this.isLocation(category)) {
                const locations = [];
                obj.rules.forEach((rule) => {
                    const { cities } = rule;
                    // Note that we use type assertion here so that typescript would
                    // properly recognize uuids type as PsgcRegionImpl
                    // due to union types.
                    const uuids = rule.uuids;
                    /**
                     * Check if location has cities
                     * if cities exist we concatenate `region` and `city`
                     * @example Agoo City, REGION I (ILOCOS REGION)
                     * else we only push city name into our location list
                     */
                    if (!!cities && cities.length >= 1) {
                        cities.forEach((city) => {
                            locations.push(`${city}, ${uuids.name}`);
                        });
                    }
                    else {
                        locations.push(uuids.name);
                    }
                });
                if (locations.length >= 1) {
                    Object.assign(customRules, { locations });
                }
            }
            else {
                /**
                 * This fallback condition were for shipping methods
                 * since we only have an array of uuids we can safely
                 * assign it to input objects
                 */
                const uuids = obj.rules.map((r) => r.uuids);
                Object.assign(customRules, {
                    [key]: flattenDeep(uuids),
                });
            }
        });
        return customRules;
    }
    mapUuids(rules) {
        return rules.map((obj) => {
            return obj.rules.map((dr) => dr.uuids);
        });
    }
    async onSubmit(form) {
        await this.create(form)
            .then(async () => {
            this.hideForm();
            const msg = successfulCreate('Customer Segmentation');
            this.customerSegmentAction('SUBMIT CREATE');
            this.successNotification(msg);
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async create(form) {
        const vm = this;
        const customRules = form.categories.filter((rule) => {
            return this.isCustom(rule.category);
        });
        const customConditions = this.buildCustomConditions(customRules);
        const nativeRules = form.categories.filter((rule) => {
            return !this.isCustom(rule.category);
        });
        const uuids = this.mapUuids(nativeRules);
        const customerSegmentUuids = flattenDeep(uuids);
        return this.$apollo.mutate({
            mutation: AppSegmentationCreate,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    name: form.name,
                    customerSegmentUuids,
                    ...customConditions,
                },
            },
            update(cache, { data }) {
                const segmentation = data.appSegmentationCreate.appSegmentation;
                const queryOptions = {
                    query: AppSegmentationsQuery,
                    variables: {
                        appUuid: vm.appUUID,
                    },
                };
                const { appSegmentations } = cache.readQuery({
                    ...queryOptions,
                });
                const segmentationNode = {
                    node: segmentation,
                    __typename: 'AppSegmentationsEdges',
                };
                const cachedSegmentations = {
                    ...appSegmentations,
                };
                cachedSegmentations.edges.push(segmentationNode);
                try {
                    cache.writeQuery({
                        ...queryOptions,
                        data: {
                            appSegmentations: cachedSegmentations,
                        },
                    });
                }
                catch (e) {
                    if (e instanceof Error) {
                        const logger = new ConsoleLogger();
                        logger.info(e.message);
                    }
                }
            },
        });
    }
};
__decorate([
    Mutation('showForm', { namespace: SEGMENT })
], CustomerSegmentationMixin.prototype, "showForm", void 0);
__decorate([
    Mutation('hideForm', { namespace: SEGMENT })
], CustomerSegmentationMixin.prototype, "hideForm", void 0);
__decorate([
    Mutation('requestOnFlight')
], CustomerSegmentationMixin.prototype, "mutationRequestOnFlight", void 0);
__decorate([
    Mutation('requestDone')
], CustomerSegmentationMixin.prototype, "requestDone", void 0);
CustomerSegmentationMixin = __decorate([
    Component
], CustomerSegmentationMixin);
export default CustomerSegmentationMixin;
